/**
 * Email validation utilities
 * Проверка формата email и блокировка disposable-доменов
 *
 * Использует:
 *  1. Пакет `disposable-email-domains` (~121k доменов, обновляется регулярно)
 *  2. Наш доп. список свежих доменов (см. EXTRA_DISPOSABLE_DOMAINS ниже)
 *
 * Чтобы обновить базовый список:  npm update disposable-email-domains
 * Чтобы добавить домен вручную:    добавь в EXTRA_DISPOSABLE_DOMAINS
 */

// eslint-disable-next-line @typescript-eslint/no-require-imports
const baseList: string[] = require('disposable-email-domains');

// Свежие/локальные домены, которые мы видели в проде, но которых может не быть
// в основном списке. Добавляй сюда новые по мере появления.
const EXTRA_DISPOSABLE_DOMAINS: string[] = [
  'maxy.de',
  'mail.tm',
  'mailbox.tm',
  'mail.gw',
  'mailto.plus',
  'fexpost.com',
  'fexbox.org',
  'mailbox.in.ua',
  'inboxbear.com',
  'rover.info',
  'chitthi.in',
  'fextemp.com',
  'any.pink',
  'merepost.com',
  'rooftest.net',
  'sigaint.org',
  'tempmail.us.com',
  'tmpmail.net',
  'mailpoof.com',
  'temporarymail.com',
  'edu.sg.com',
  'easymail.host',
  'wuuvo.com',
];

const DISPOSABLE_DOMAINS: Set<string> = new Set([
  ...baseList.map((d) => d.toLowerCase()),
  ...EXTRA_DISPOSABLE_DOMAINS.map((d) => d.toLowerCase()),
]);

/**
 * Проверяет формат email (RFC-совместимая базовая проверка)
 */
export function isValidEmailFormat(email: string): boolean {
  if (!email || typeof email !== 'string') return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return false;

  const [localPart, domain] = email.split('@');
  if (!localPart || localPart.length === 0) return false;
  if (!domain || !domain.includes('.')) return false;
  if (domain.startsWith('.') || domain.endsWith('.')) return false;
  if (domain.includes('..')) return false;
  if (email.length > 254) return false;
  if (localPart.length > 64) return false;

  return true;
}

/**
 * Извлекает домен из email
 */
function extractDomain(email: string): string | null {
  if (!email || typeof email !== 'string') return null;
  const parts = email.toLowerCase().trim().split('@');
  if (parts.length !== 2) return null;
  return parts[1] || null;
}

/**
 * Проверяет, является ли домен disposable.
 * Учитывает точное совпадение и поддомены (mail.temp-mail.org → temp-mail.org).
 */
export function isDisposableEmail(email: string): boolean {
  const domain = extractDomain(email);
  if (!domain) return false;

  if (DISPOSABLE_DOMAINS.has(domain)) return true;

  // Проверка поддоменов: если корневой домен в списке — блокируем
  const parts = domain.split('.');
  for (let i = 1; i < parts.length - 1; i++) {
    const sub = parts.slice(i).join('.');
    if (DISPOSABLE_DOMAINS.has(sub)) return true;
  }

  return false;
}

/**
 * Полная валидация email (формат + disposable)
 */
export function validateEmail(email: string): {
  isValid: boolean;
  error?: string;
} {
  if (!email || typeof email !== 'string' || email.trim().length === 0) {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim().toLowerCase();

  if (!isValidEmailFormat(trimmedEmail)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  if (isDisposableEmail(trimmedEmail)) {
    return {
      isValid: false,
      error:
        'Disposable email addresses are not allowed. Please use a permanent email address.',
    };
  }

  return { isValid: true };
}

/**
 * Возвращает размер списка disposable доменов (для диагностики/тестов)
 */
export function getDisposableDomainsCount(): number {
  return DISPOSABLE_DOMAINS.size;
}
